import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, RefreshControl } from 'react-native'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { colors, fonts, radius } from '../../lib/theme'
import ErrorBoundary from '../../components/ErrorBoundary'
import { useThemeColors } from '../../lib/useThemeColors'
import { SectionLabel } from '../../components/Leaderboard/ui'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { perfMark, perfReset, perfSpan } from '../../lib/perfTrace'
import RaceSwitcher from '../../components/Pick/RaceSwitcher'
import StageSwitcher from '../../components/Pick/StageSwitcher'
import PickModal from '../../components/Leaderboard/PickModal'
import PickModalGenerale from '../../components/Leaderboard/PickModalGenerale'
import TeamPickModal from '../../components/Leaderboard/TeamPickModal'
import ClassificaSingola from '../../components/Leaderboard/ClassificaSingola'
import ClassificaTour from '../../components/Leaderboard/ClassificaTour'
import ClassificaTeam from '../../components/Leaderboard/ClassificaTeam'
import ClassificaPietre from '../../components/Leaderboard/ClassificaPietre'
import SegmentedTabs from '../../components/SegmentedTabs'
import { JerseyAsOf } from '../../lib/useSpecialJerseys'
import { ListSkeleton, ErrorState } from '../../components/StateViews'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { romeToday } from '../../lib/romeTime'

const PIETRE_LEAGUE_ID = 4

// Sceglie una tappa di default sensata quando non c'è un target esplicito
// (es. cambio gara manuale): l'ultima tappa che ha risultati in fact_results
// (stesso criterio SQL di fn_last_race_id_stage_with_results) — non la data,
// che dice solo quando la tappa è calendarizzata, non se è stata disputata
// e i risultati caricati (possono arrivare ore dopo il traguardo).
function pickCurrentTappa(opts) {
  const conRisultati = opts.filter(o => o.hasResults)
  if (conRisultati.length > 0) return conRisultati[conRisultati.length - 1] // già ordinate per stageNumber
  return opts[0] ?? null
}

// Riga dei tempi del caricamento (solo admin): quanto ci mettono rete (gare/tappe), classifica, avatar e disegno
function PerfLine({ tc }) {
  const [, tick] = useState(0)
  useEffect(() => { const id = setInterval(() => tick(n => n + 1), 700); return () => clearInterval(id) }, [])
  const f = (v) => (v == null ? '–' : `${(v / 1000).toFixed(2)}s`)
  return (
    <Text style={{ fontSize: 11, color: tc.textMuted, textAlign: 'center', marginTop: 10, marginHorizontal: 12 }}>
      {`⏱ gare/tappe ${f(perfSpan('mount', 'init_done'))} · classifica (rete) ${f(perfSpan('tour_start', 'tour_rpc_done'))} · lista (disegno) ${f(perfSpan('tour_rpc_done', 'list_committed'))} · avatar (rete) ${f(perfSpan('avatars_start', 'avatars_done'))} · completa ${f(perfSpan('tour_start', 'avatars_done'))}`}
    </Text>
  )
}

export default function Leaderboard() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { user, leagueId, userLeagues, isAdmin } = useAuth()
  const params = useLocalSearchParams()
  const insets = useSafeAreaInsets()

  // Bug corretto (stesso trovato stamattina nel tab Pick): dipendenze vuote
  // facevano calcolare preRaceId/preStageId una sola volta al primo mount,
  // ignorando navigazioni successive con parametri diversi.
  const preRaceId = useMemo(() => params.id_race ? parseInt(params.id_race) : null, [params.id_race])
  const preStageId = useMemo(() => params.id_stage ? parseInt(params.id_stage) : null, [params.id_stage])
  const preScope = params.scope ?? null

  const [gare, setGare] = useState([])
  const [tappe, setTappe] = useState([])
  const [selGara, setSelGara] = useState(null)
  const [selTappa, setSelTappa] = useState(null)
  const [tappeAnnullate, setTappeAnnullate] = useState([])   // tappe annullate della gara selezionata (dim_race_stage_cancelled)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [mainTab, setMainTab] = useState('classifica')
  const [modalPlayer, setModalPlayer] = useState(null)
  const [modalTeam, setModalTeam] = useState(null)
  const [refreshing, setRefreshing] = useState(false)

  const manualRaceIdRef = useRef(null)
  const manualStageIdRef = useRef(null)

  const tappeCache = useRef({})
  const oneDayCache = useRef({})   // gare di un giorno: id_race -> unica tappa

  const currentLeague = userLeagues?.find(l => l.id_league === leagueId)
  const hasTeam = currentLeague?.team_race ?? false
  const isPietre = leagueId === PIETRE_LEAGUE_ID

  const loadTappe = useCallback(async (gara, targetStageId = null) => {
    // Gara di un giorno gia vista: la tappa unica sta nella sua cache (la lista tappe e' vuota per definizione)
    if (gara.type === 3 && oneDayCache.current[gara.value]) {
      setTappe([])
      setSelTappa(oneDayCache.current[gara.value])
      return
    }
    if (gara.type !== 3 && tappeCache.current[gara.value]) {
      const cached = tappeCache.current[gara.value]
      setTappe(cached)
      const initTappa = cached.find(t => t.value === targetStageId) ?? pickCurrentTappa(cached)
      setSelTappa(initTappa?.data ?? null)
      return
    }
    if (gara.type === 3) {
      const { data } = await supabase.from('dim_race_stage').select('id_stage, id_stage_number').eq('id_race', gara.value).limit(1)
      setTappe([])
      setSelTappa(data?.[0] ?? null)
      if (data?.[0]) oneDayCache.current[gara.value] = data[0]
      tappeCache.current[gara.value] = []
    } else {
      const { data } = await supabase.from('dim_race_stage').select('id_stage, id_stage_number, stage_date').eq('id_race', gara.value).order('id_stage_number')
      // Stesso problema di init: fact_results ha una riga per corridore per
      // tappa (migliaia di righe anche per una sola gara) — la select grezza
      // veniva troncata dal Max Rows di Supabase (1000). Uso la stessa RPC
      // che restituisce solo id_stage distinti con risultati.
      const { data: resultsData } = await supabase.rpc('fn_stages_with_results', { p_id_races: [gara.value] })
      const stageIdsConRisultati = new Set((resultsData ?? []).map(r => r.id_stage))
      const tappeOpts = (data ?? []).map(tappa => ({
        value: tappa.id_stage,
        label: `${t('common.stage')} ${tappa.id_stage_number}`,
        stageNumber: tappa.id_stage_number,
        hasResults: stageIdsConRisultati.has(tappa.id_stage),
        data: tappa,
      }))
      tappeCache.current[gara.value] = tappeOpts
      setTappe(tappeOpts)
      const initTappa = tappeOpts.find(t => t.value === targetStageId) ?? pickCurrentTappa(tappeOpts)
      setSelTappa(initTappa?.data ?? null)
    }
  }, [t])

  useEffect(() => {
    let cancelled = false
    tappeCache.current = {}
    oneDayCache.current = {}
    perfReset(); perfMark('mount')
    const init = async () => {
      setLoadError(false)
      const raceRes = await supabase.rpc('fn_league_races', { p_id_league: leagueId })
      if (cancelled) return
      if (raceRes.error) { setLoadError(true); setLoading(false); return }

      const racesData = raceRes.data ?? []
      if (racesData.length === 0) { setLoading(false); return }

      // Gare "da mostrare" (non collassate): quelle con end_date reale
      // (`dim_race.end_date`, non calcolata) uguale alla end_date MASSIMA
      // tra le gare che hanno ALMENO 1 risultato in fact_results — replica
      // esatta della query validata dall'utente. Possono essere più di una
      // in parallelo (es. Tour de France + Last Chance, stesso end_date).
      // Pallino verde solo se non ancora del tutto conclusa: end_date nel
      // futuro, OPPURE l'ultima tappa con risultati non è l'ultima tappa
      // della gara (es. Tour de France tappa 21 non ancora disputata).
      const raceIds = racesData.map(g => g.id_race)
      // stile della maglia GPM (pois) per gara: query a parte e tollerante (se la colonna non c'e ancora, maglia a tinta unita)
      const patternsRes = await supabase.from('dim_race').select('id_race, gm_pattern').in('id_race', raceIds)
      const gmPatternByRace = Object.fromEntries((patternsRes.data ?? []).map(r => [r.id_race, r.gm_pattern]))
      const [stagesRes, resultsRes] = await Promise.all([
        supabase.from('dim_race_stage').select('id_race, id_stage, id_stage_number').in('id_race', raceIds).limit(20000),
        // fn_stages_with_results: righe DISTINTE id_race/id_stage (non una
        // riga per corridore) — evita il Max Rows di Supabase (1000) che
        // troncava fact_results e faceva sparire gare con molti risultati.
        supabase.rpc('fn_stages_with_results', { p_id_races: raceIds }),
      ])
      if (cancelled) return

      const stageNumberById = {}
      const maxStageNumberByRace = {}
      for (const s of (stagesRes.data ?? [])) {
        stageNumberById[s.id_stage] = s.id_stage_number
        maxStageNumberByRace[s.id_race] = Math.max(maxStageNumberByRace[s.id_race] ?? 0, s.id_stage_number)
      }
      const resultStageIdsByRace = {}
      for (const r of (resultsRes.data ?? [])) (resultStageIdsByRace[r.id_race] ??= new Set()).add(r.id_stage)
      const maxResultStageNumberByRace = {}
      for (const [raceId, stageIds] of Object.entries(resultStageIdsByRace)) {
        maxResultStageNumberByRace[raceId] = Math.max(...[...stageIds].map(id => stageNumberById[id] ?? 0))
      }

      const todayStr = romeToday()
      const gareOpts = racesData.map(g => ({
        value: g.id_race, label: g.name, type: g.id_type_race,
        colors: { gc: g.color_gc, pt: g.color_pt, gm: g.color_gm, gmPattern: gmPatternByRace[g.id_race] ?? null },
        raceDate: g.race_date,
        // last_stage_date = dim_race.end_date reale. Per le gare di un giorno e' spesso NULL: la fine coincide
        // con la data di partenza. Senza questo, quelle gare non entravano mai nel confronto "ultima gara
        // con risultati" e la classifica si apriva su una gara a tappe piu vecchia (es. la Vuelta).
        lastStageDate: g.last_stage_date ?? g.race_date,
        hasResults: (resultStageIdsByRace[g.id_race]?.size ?? 0) > 0,
      }))

      const endDatesConRisultati = gareOpts.filter(g => g.hasResults).map(g => g.lastStageDate).filter(Boolean)
      const maxEndDate = endDatesConRisultati.length > 0 ? endDatesConRisultati.reduce((a, b) => a > b ? a : b) : null

      const gareFinali = gareOpts.map(g => {
        const daMostrare = g.hasResults && g.lastStageDate === maxEndDate
        const nonAncoraConclusa = (g.lastStageDate && g.lastStageDate > todayStr)
          || maxResultStageNumberByRace[g.value] !== maxStageNumberByRace[g.value]
        return { ...g, isActiveRace: daMostrare && nonAncoraConclusa, status: daMostrare ? 'attiva' : (g.hasResults ? 'passata' : 'futura') }
      })
      setGare(gareFinali)

      const targetRaceId = preRaceId ?? manualRaceIdRef.current ?? gareFinali.find(g => g.status === 'attiva')?.value
      const targetStageId = preStageId ?? manualStageIdRef.current ?? null

      const initGara = gareFinali.find(g => g.value === targetRaceId) ?? gareFinali[0]
      if (initGara.type === 3) setMainTab('classifica')
      setSelGara(initGara)

      await loadTappe(initGara, targetStageId)
      if (!cancelled) { setLoading(false); perfMark('init_done') }
    }
    init().catch(() => { if (!cancelled) { setLoadError(true); setLoading(false) } })
    return () => { cancelled = true }
  }, [leagueId, loadTappe, preRaceId, preStageId, reloadKey])

  const onGaraSelect = useCallback(async (item) => {
    // gia selezionata: niente da ricaricare (e la classifica non deve sfarfallare)
    if (item.value === selGara?.value && selTappa) return
    setSelTappa(null)
    setSelGara(item)
    if (item.type === 3) setMainTab('classifica')
    manualRaceIdRef.current = item.value
    manualStageIdRef.current = null
    await loadTappe(item)
  }, [loadTappe, selGara?.value, selTappa])

  const onTappaSelect = useCallback((item) => {
    setSelTappa(item.data)
    manualStageIdRef.current = item.data?.id_stage ?? null
  }, [])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    tappeCache.current = {}
    oneDayCache.current = {}
    if (selGara) await loadTappe(selGara, selTappa?.id_stage)
    setRefreshing(false)
  }, [selGara, selTappa, loadTappe])

  const isOneDay = selGara?.type === 3

  // tappe annullate: non esistono piu' tra le tappe, ma si scrive "Tappa N annullata" (sql/20261028150000_tappe_annullate.sql)
  useEffect(() => {
    if (!selGara?.value || selGara.type === 3) { setTappeAnnullate([]); return }
    let alive = true
    supabase.from('dim_race_stage_cancelled').select('id_stage_number').eq('id_race', selGara.value).order('id_stage_number')
      .then(({ data, error }) => { if (alive) setTappeAnnullate(error ? [] : (data ?? []).map(x => x.id_stage_number)) })
    return () => { alive = false }
  }, [selGara?.value, selGara?.type, reloadKey])

  // ── Gare: passate / attive (da mostrare) / future ─────────────────────
  // Status e isActiveRace (dot) già calcolati in init secondo la query SQL
  // validata dall'utente (fact_results + end_date reale di dim_race).
  // Selettore gara (stesso di Pick): in linea le gare attive (con risultati recenti), le altre nello storico.
  const MAX_RACE_PILLS = 3
  const raceInfo = gare.map(g => ({
    ...g,
    kind: g.status, // 'attiva' | 'passata' | 'futura' (calcolato in init)
    status: g.status === 'attiva' ? (g.isActiveRace ? 'live' : 'done') : g.status === 'passata' ? 'done' : 'pick', // pick = non ancora iniziata
    date: g.raceDate,
  }))
  const gareAttive = raceInfo.filter(g => g.kind === 'attiva')
  const garePassate = raceInfo.filter(g => g.kind === 'passata').reverse() // dalla piu' recente
  const selRace = raceInfo.find(g => g.value === selGara?.value)
  let racePills = gareAttive.slice(0, MAX_RACE_PILLS)
  // la gara selezionata si vede sempre: se e' passata o futura prende l'ultimo posto in linea
  if (selRace && !racePills.some(r => r.value === selRace.value)) racePills = [...racePills.slice(0, MAX_RACE_PILLS - 1), selRace]
  if (!racePills.length && garePassate.length) racePills = [garePassate[0]]
  const shownIds = new Set(racePills.map(r => r.value))
  // le gare future (senza risultati) non vanno in classifica: solo le attive non gia' in linea
  const racesNextMore = gareAttive.filter(r => !shownIds.has(r.value))
  const racesPastMore = garePassate.filter(r => !shownIds.has(r.value))

  // ── Tappe della gara selezionata: solo quelle con risultati caricati ─────
  // `hasResults` viene da fact_results (impostato in loadTappe), non dalla
  // data: una tappa "di oggi" può non avere ancora risultati (arrivano solo
  // ore dopo il traguardo). Le tappe senza risultati non compaiono affatto.
  const tappeConRisultati = isOneDay ? [] : tappe.filter(tp => tp.hasResults)
  const stageItems = tappeConRisultati.map(tp => ({
    value: tp.value, number: tp.stageNumber, date: tp.data?.stage_date, status: 'done', data: tp.data,
  }))

  useEffect(() => { if (isOneDay) setMainTab('classifica') }, [isOneDay])

  // tappa precedente (con risultati) della stessa gara: serve per le frecce di variazione nelle classifiche generali
  const prevStageId = (() => {
    const i = stageItems.findIndex(x => x.value === selTappa?.id_stage)
    return i > 0 ? stageItems[i - 1].value : null
  })()

  if (loading) return (
    <View style={[styles.loadingWrap, { paddingHorizontal: 0 }]}>
      <ListSkeleton />
    </View>
  )
  if (loadError) return (
    <View style={[styles.loadingWrap, { justifyContent: 'center' }]}>
      <ErrorState onRetry={() => { setLoading(true); setReloadKey(k => k + 1) }} />
    </View>
  )

  // Tab disponibili
  const tabs = [
    { key: 'classifica', label: t('leaderboard.tab_individual') },
    ...(hasTeam ? [{ key: 'team', label: t('leaderboard.tab_team') }] : []),
    ...(isPietre ? [{ key: 'pietre', label: '🪨 Pietre' }] : []),
  ]

  return (
    // le maglie speciali degli avatar valgono alla data della tappa mostrata, non a oggi
    <JerseyAsOf date={selTappa?.stage_date ?? selGara?.raceDate} stageId={selTappa?.id_stage}>
    <ScrollView
      style={[styles.container, { backgroundColor: tc.bg }]}
      contentContainerStyle={{ paddingBottom: 32 + insets.bottom }}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={tc.rosa} colors={[tc.rosa]} />}
    >
      <View style={styles.sectionLabelWrap}>
        <SectionLabel>{t('leaderboard.title')}</SectionLabel>
      </View>

      {/* Tab bar — visibile se ci sono più di 1 tab */}
      {tabs.length > 1 && (
        <SegmentedTabs variant="underline" options={tabs} value={mainTab} onChange={setMainTab} style={{ marginHorizontal: 16, marginTop: 10 }} />
      )}

      {/* Selettori gara/tappa — solo per classifica normale */}
      {mainTab !== 'pietre' && (
        <>
          <RaceSwitcher
            pills={racePills}
            nextMore={racesNextMore}
            past={racesPastMore}
            selected={selGara?.value}
            onSelect={onGaraSelect}
            labels={{ live: t('leaderboard.race_tag_live'), done: t('leaderboard.race_tag_done'), pick: t('leaderboard.race_tag_soon') }}
          />
          {stageItems.length > 0 && (
            <StageSwitcher stages={stageItems} selected={selTappa?.id_stage} onSelect={onTappaSelect} hideStatus />
          )}
          {tappeAnnullate.map(n => (
            <View key={n} style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginHorizontal: 16, marginTop: 8 }}>
              <MaterialCommunityIcons name="cancel" size={14} color={tc.textMuted} />
              <Text style={{ fontSize: 12, color: tc.textMuted }}>{t('leaderboard.stage_cancelled', { n })}</Text>
            </View>
          ))}
          <View style={[styles.divider, { marginBottom: 16 }]} />
        </>
      )}

      {/* Classifica normale */}
      {mainTab === 'classifica' && selTappa && selGara && (
        isOneDay
          ? <ClassificaSingola
            key={`${selTappa.id_stage}_${leagueId}`}
            idStage={selTappa.id_stage}
            leagueId={leagueId}
            currentUserId={user?.id}
            onPlayerClick={setModalPlayer}
            raceName={selGara?.label}
          />
          : <ErrorBoundary label="ClassificaTour">
            <ClassificaTour
              key={`${selTappa.id_stage}_${leagueId}`}
              idStage={selTappa.id_stage}
              stageNumber={selTappa.id_stage_number}
              prevStageId={prevStageId}
              leagueId={leagueId}
              colors={selGara.colors}
              currentUserId={user?.id}
              onPlayerClick={setModalPlayer}
              initialScope={preScope}
              raceName={selGara?.label}
            />
          </ErrorBoundary>
      )}

      {/* Classifica team */}
      {mainTab === 'team' && hasTeam && selTappa && (
        <ClassificaTeam
          leagueId={leagueId}
          idStage={selTappa.id_stage}
          stageNumber={selTappa.id_stage_number}
          isOneDay={isOneDay}
          onTeamClick={setModalTeam}
        />
      )}

      {/* Classifica Pietre */}
      {mainTab === 'pietre' && isPietre && (
        <ClassificaPietre currentUserId={user?.id} />
      )}

      {modalPlayer && selTappa && (
        // Generale di un giro: non le pick di una sola tappa ma tappa per tappa come ci e' arrivato
        !isOneDay && modalPlayer.scope === 'generale'
          ? <PickModalGenerale
            player={modalPlayer}
            idStage={selTappa.id_stage}
            leagueId={leagueId}
            raceId={selGara?.value}
            onClose={() => setModalPlayer(null)}
          />
          : <PickModal
            player={modalPlayer}
            idStage={selTappa.id_stage}
            leagueId={leagueId}
            raceId={selGara?.value}
            isOneDay={isOneDay}
            onClose={() => setModalPlayer(null)}
          />
      )}

      {modalTeam && selTappa && (
        <TeamPickModal
          team={modalTeam}
          idStage={selTappa.id_stage}
          leagueId={leagueId}
          raceId={selGara?.value}
          isOneDay={isOneDay}
          onClose={() => setModalTeam(null)}
        />
      )}
      {isAdmin && mainTab === 'classifica' && <PerfLine tc={tc} />}
    </ScrollView>
    </JerseyAsOf>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1 },
  loadingWrap: { flex: 1, padding: 20, backgroundColor: tc.bg },
  sectionLabelWrap: { padding: 16, paddingBottom: 0 },
  divider: { height: 1, backgroundColor: tc.border, marginHorizontal: 16 },
})